import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import DragIndicator from "@mui/icons-material/DragIndicator";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { LoadingButton } from "@mui/lab";
import { normalizeExam2Values } from "./exam2Catalog";

const cloneRows = (systems = [], preferredBySystemId = {}) =>
  (Array.isArray(systems) ? systems : []).map((row) => ({
    ...row,
    options: [...(row.options || [])],
    normalOptions: [...(row.normalOptions || [])],
    value: normalizeExam2Values(row.value),
    hidden: Boolean(row.hidden),
    preferredNormal: String(
      preferredBySystemId?.[row.id] || row.preferredNormal || "",
    ).trim(),
  }));

const moveItem = (list, index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
};

const patchRow = (rows, index, patch) =>
  rows.map((item, i) => (i === index ? { ...item, ...patch } : item));

/**
 * Option 2 Customize exam modal: This note | My defaults.
 * Edit section order/labels/visibility and per-section finding options + preferred normal.
 */
const CustomizeExamModal = ({
  open,
  onClose,
  systems = [],
  preferredBySystemId = {},
  scopeLabel = "",
  onSaveThisNote,
  onSaveDefaults,
}) => {
  const [tab, setTab] = useState(0);
  const [noteRows, setNoteRows] = useState(() => cloneRows(systems, preferredBySystemId));
  const [defaultRows, setDefaultRows] = useState(() =>
    cloneRows(systems, preferredBySystemId),
  );
  const [expandedId, setExpandedId] = useState(null);
  const [draftOption, setDraftOption] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const cloned = cloneRows(systems, preferredBySystemId);
    setNoteRows(cloned);
    setDefaultRows(cloned.map((row) => ({ ...row, options: [...row.options], normalOptions: [...row.normalOptions], value: [...row.value] })));
    setTab(0);
    setExpandedId(null);
    setDraftOption("");
    setSaving(false);
  }, [open, systems, preferredBySystemId]);

  const activeRows = tab === 0 ? noteRows : defaultRows;
  const setActiveRows = tab === 0 ? setNoteRows : setDefaultRows;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (tab === 0) await onSaveThisNote?.(activeRows);
      else await onSaveDefaults?.(activeRows);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Customize exam</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_, value) => {
            setTab(value);
            setExpandedId(null);
            setDraftOption("");
          }}
          sx={{ mb: 2 }}
        >
          <Tab label="This note" />
          <Tab label="My defaults" />
        </Tabs>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {tab === 1
            ? `Changes become your personal template${scopeLabel ? ` for ${scopeLabel}` : ""}, including each section’s finding options.`
            : "Reorder or hide systems for this encounter, or expand a section to edit its options."}
        </Typography>

        <Stack spacing={1}>
          {activeRows.map((row, index) => {
            const expanded = expandedId === row.id;
            const options = Array.isArray(row.options) ? row.options : [];
            return (
              <Paper
                key={row.id}
                variant="outlined"
                sx={{ p: 1.5, opacity: row.hidden ? 0.55 : 1 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      aria-label={`Move ${row.name} up`}
                      disabled={index === 0}
                      onClick={() => setActiveRows(moveItem(activeRows, index, -1))}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <DragIndicator sx={{ color: "text.secondary", fontSize: 18 }} />
                    <IconButton
                      size="small"
                      aria-label={`Move ${row.name} down`}
                      disabled={index === activeRows.length - 1}
                      onClick={() => setActiveRows(moveItem(activeRows, index, 1))}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    size="small"
                    label="Section"
                    value={row.name}
                    onChange={(event) =>
                      setActiveRows(patchRow(activeRows, index, { name: event.target.value }))
                    }
                    sx={{ flex: 1, minWidth: 120 }}
                  />
                  <Tooltip title={row.hidden ? "Show on exam" : "Hide from exam"}>
                    <IconButton
                      size="small"
                      aria-label={row.hidden ? `Show ${row.name}` : `Hide ${row.name}`}
                      onClick={() =>
                        setActiveRows(patchRow(activeRows, index, { hidden: !row.hidden }))
                      }
                    >
                      {row.hidden ? (
                        <VisibilityOffOutlined fontSize="small" />
                      ) : (
                        <VisibilityOutlined fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                    onClick={() => {
                      setExpandedId(expanded ? null : row.id);
                      setDraftOption("");
                    }}
                  >
                    Options ({options.length})
                  </Button>
                </Box>

                {expanded ? (
                  <Stack spacing={1} sx={{ mt: 1.5, pl: { xs: 0, sm: 6 } }}>
                    <Typography variant="caption" color="text.secondary">
                      Edit findings shown in this section. Star marks your preferred normal for
                      Tab fill.
                    </Typography>
                    {options.map((option, optionIndex) => (
                      <Box
                        key={`${row.id}-${optionIndex}`}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <IconButton
                            size="small"
                            aria-label="Move option up"
                            disabled={optionIndex === 0}
                            onClick={() =>
                              setActiveRows(
                                patchRow(activeRows, index, {
                                  options: moveItem(options, optionIndex, -1),
                                }),
                              )
                            }
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="Move option down"
                            disabled={optionIndex === options.length - 1}
                            onClick={() =>
                              setActiveRows(
                                patchRow(activeRows, index, {
                                  options: moveItem(options, optionIndex, 1),
                                }),
                              )
                            }
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </Box>
                        <TextField
                          size="small"
                          value={option}
                          onChange={(event) => {
                            const nextOptions = options.map((text, i) =>
                              i === optionIndex ? event.target.value : text,
                            );
                            const preferredNormal =
                              row.preferredNormal === option
                                ? event.target.value
                                : row.preferredNormal;
                            const normalOptions = (row.normalOptions || []).map((text) =>
                              text === option ? event.target.value : text,
                            );
                            setActiveRows(
                              patchRow(activeRows, index, {
                                options: nextOptions,
                                normalOptions,
                                preferredNormal,
                              }),
                            );
                          }}
                          fullWidth
                        />
                        <Tooltip title="Preferred normal">
                          <IconButton
                            size="small"
                            color={row.preferredNormal === option ? "primary" : "default"}
                            aria-label={`Set ${option} as preferred normal`}
                            onClick={() => {
                              const normalOptions = [
                                ...new Set([...(row.normalOptions || []), option]),
                              ];
                              setActiveRows(
                                patchRow(activeRows, index, {
                                  preferredNormal: option,
                                  normalOptions,
                                }),
                              );
                            }}
                          >
                            {row.preferredNormal === option ? (
                              <Star fontSize="small" />
                            ) : (
                              <StarBorder fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${option}`}
                          onClick={() => {
                            const nextOptions = options.filter((_, i) => i !== optionIndex);
                            const normalOptions = (row.normalOptions || []).filter(
                              (text) => text !== option,
                            );
                            const preferredNormal =
                              row.preferredNormal === option
                                ? nextOptions[0] || ""
                                : row.preferredNormal;
                            setActiveRows(
                              patchRow(activeRows, index, {
                                options: nextOptions,
                                normalOptions,
                                preferredNormal,
                              }),
                            );
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <TextField
                        size="small"
                        label="New option"
                        value={draftOption}
                        onChange={(event) => setDraftOption(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key !== "Enter") return;
                          event.preventDefault();
                          const text = draftOption.trim();
                          if (!text) return;
                          setActiveRows(
                            patchRow(activeRows, index, {
                              options: [...options, text],
                            }),
                          );
                          setDraftOption("");
                        }}
                        fullWidth
                      />
                      <Button
                        size="small"
                        startIcon={<Add />}
                        disabled={!draftOption.trim()}
                        onClick={() => {
                          const text = draftOption.trim();
                          if (!text) return;
                          setActiveRows(
                            patchRow(activeRows, index, {
                              options: [...options, text],
                            }),
                          );
                          setDraftOption("");
                        }}
                      >
                        Add
                      </Button>
                    </Stack>
                  </Stack>
                ) : null}
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton loading={saving} onClick={handleSave}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CustomizeExamModal;
